// useMyPerms — single hook that fetches the acting admin's roles +
// permission codes via /admin/me/permissions and exposes a tiny
// matcher API (has / hasAny / inRole). The Sidebar uses it to render
// a permission-aware nav; the actual authorisation is still on the
// server (every /admin/* mutation is gated by the same codes).
//
// While the request is in-flight we return loaded=false; callers
// generally render the nav as "show everything" in that case so the
// admin doesn't get a janky empty sidebar on first paint.
import { useQuery } from "@tanstack/react-query";
import { fetchMyPermissions, keys } from "@/lib/queries";
import { Role, type PermsView } from "@/lib/perms";

export function useMyPerms(): PermsView {
  const q = useQuery({
    queryKey: keys.myPerms(),
    queryFn: fetchMyPermissions,
    staleTime: 60_000,
    retry: 1,
  });
  const roles = q.data?.roles ?? [];
  const permissions = q.data?.permissions ?? [];
  const isSuperAdmin = roles.includes(Role.SuperAdmin);
  const permSet = new Set(permissions);
  return {
    loaded: q.isSuccess,
    adminId: q.data?.admin_id ?? "",
    roles,
    permissions,
    isSuperAdmin,
    has: (code) => isSuperAdmin || permSet.has(code),
    hasAny: (...codes) =>
      isSuperAdmin || codes.some((c) => permSet.has(c)),
    inRole: (role) => roles.includes(role),
  };
}
